/**
 * Map Style Control for COP Dashboard
 * Allows users to switch between different Mapbox map styles
 */

import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  Paper,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Tooltip
} from '@mui/material';
import { Layers as LayersIcon, Close as CloseIcon } from '@mui/icons-material';

export type MapStyleType = 'streets' | 'satellite' | 'satellite-streets' | 'dark' | 'light' | 'outdoors';

interface MapStyleOption {
  value: MapStyleType;
  label: string;
  thumbnail: string;
  description: string;
}

interface MapStyleControlProps {
  selectedMapStyle: MapStyleType;
  onMapStyleChange: (mapStyle: MapStyleType) => void;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const mapStyleOptions: MapStyleOption[] = [
  {
    value: 'streets',
    label: 'Streets',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Street map'
  },
  {
    value: 'satellite',
    label: 'Satellite',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Satellite imagery'
  },
  {
    value: 'satellite-streets',
    label: 'Satellite Streets',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Satellite with streets'
  },
  {
    value: 'dark',
    label: 'Dark',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Dark theme'
  },
  {
    value: 'light',
    label: 'Light',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Light theme'
  },
  {
    value: 'outdoors',
    label: 'Outdoors',
    thumbnail: `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/-110.9265,32.2217,11,0/100x100@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    description: 'Outdoor/hiking map'
  }
];

const MapStyleControl: React.FC<MapStyleControlProps> = ({
  selectedMapStyle,
  onMapStyleChange,
  position = 'top-right'
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStyleSelect = (mapStyle: MapStyleType) => {
    onMapStyleChange(mapStyle);
    handleClose();
  };

  // Position styles
  const getPositionStyles = () => {
    const base = {
      position: 'absolute' as const,
      zIndex: 1000,
      margin: '10px'
    };

    switch (position) {
      case 'top-left':
        return { ...base, top: 0, left: 0 };
      case 'top-right':
        return { ...base, top: 0, right: 0 };
      case 'bottom-left':
        return { ...base, bottom: 0, left: 0 };
      case 'bottom-right':
        return { ...base, bottom: 0, right: 0 };
      default:
        return { ...base, top: 0, right: 0 };
    }
  };

  const selectedOption = mapStyleOptions.find(option => option.value === selectedMapStyle);

  return (
    <>
      <Box sx={getPositionStyles()}>
        <Tooltip title="Change Map Style">
          <IconButton
            onClick={handleClick}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#333',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              width: 40,
              height: 40
            }}
          >
            <LayersIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Paper sx={{ width: 320, maxWidth: '90vw' }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            p: 2, 
            borderBottom: '1px solid #e0e0e0' 
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              Map Style
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Current Selection */}
          {selectedOption && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current: <strong>{selectedOption.label}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedOption.description}
              </Typography>
            </Box>
          )}

          {/* Map Style Grid */}
          <Box sx={{ p: 2 }}>
            <Grid container spacing={1}>
              {mapStyleOptions.map((option) => (
                <Grid item xs={4} key={option.value}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedMapStyle === option.value ? '2px solid #2196f3' : '1px solid #e0e0e0',
                      '&:hover': {
                        border: '2px solid #2196f3',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => handleStyleSelect(option.value)}
                  >
                    <CardMedia
                      component="img"
                      height="60"
                      image={option.thumbnail}
                      alt={option.label}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.7rem', 
                        fontWeight: selectedMapStyle === option.value ? 'bold' : 'normal',
                        color: selectedMapStyle === option.value ? '#2196f3' : 'inherit'
                      }}>
                        {option.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default MapStyleControl;
